import { useCallback, useMemo, useState } from "react";

export interface UseBulkSelectionResult {
  selectedIds: Set<string>;
  has: (id: string) => boolean;
  toggleOne: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
  allSelected: boolean;
  someSelected: boolean;
  count: number;
  ids: string[];
}

export function useBulkSelection<T>({
  items,
  getId,
}: {
  items: T[];
  getId: (item: T) => string;
}): UseBulkSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const has = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(getId(it)));
  const someSelected = selectedIds.size > 0;

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (items.length > 0 && items.every((it) => prev.has(getId(it)))) {
        return new Set();
      }
      return new Set(items.map(getId));
    });
  }, [items, getId]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const ids = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return {
    selectedIds,
    has,
    toggleOne,
    toggleAll,
    clear,
    allSelected,
    someSelected,
    count: selectedIds.size,
    ids,
  };
}
